import { Button, Grid, Input, Modal, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import Common from "../../Refinery/CommonRender";
import { vec3 } from "gl-matrix";


export type SceneSettingsData = {
  backgroundColour?: [number, number, number];
}

export type SceneSettingsModalProps = {
  open: boolean;
  data: SceneSettingsData;

  onSceneSettingsChange: (data: SceneSettingsData) => void;
  onDownload: () => void;
  onUpload: (data: string) => void;
  onClose: () => void;
};

export const SceneSettingsModal = (props: SceneSettingsModalProps) => {
  const [fileName, setFileName] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.readAsText(file);

      reader.onload = () => {
        if (reader.result) {
          props.onUpload(reader.result?.toString());
          props.onClose();
        }
      };

      reader.onerror = () => {
        console.error("Failed to read file:", reader.error);
        throw reader.error;
      };
    } catch (err) {
      // TODO - display error
      console.error(err);
    }
  };

  const handleClose = () => {
    setFileName('');
    props.onClose();
  };

  const backgroundValue = useMemo(() => {
    if (props.data.backgroundColour) {
      return Common.rgbToHex(vec3.fromValues(props.data.backgroundColour[0], props.data.backgroundColour[1], props.data.backgroundColour[2]));
    }
    return Common.rgbToHex(vec3.create());
  }, [props.data]);

  return (
    <Modal
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      open={props.open}
      onClose={handleClose}
    >
      <Grid
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          padding: 3,
          minWidth: 400,
          boxShadow: 24,
          outline: 'none',
        })}
        spacing={4}
        container
      >
        <Grid size={12}>
          <Typography color="textPrimary" variant="h6">Scene Settings</Typography>
        </Grid>
        <Grid size={12}>
          <Typography color="textPrimary">Background Colour</Typography>
          <Input
            type='color'
            sx={{ width: '40px' }}
            value={backgroundValue}
            onChange={(e) => {
              const converted = Common.hexToRGB(e.target.value);
              props.onSceneSettingsChange({ backgroundColour: [converted[0], converted[1], converted[2]] });
            }}
          />
        </Grid>
        <Grid size={12}>
          <input
            id="file-upload"
            type="file"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <label htmlFor="file-upload">
            <Button
              variant="contained"
              component="span"
              startIcon={<UploadIcon />}
            >
              {fileName ? `Uploaded: ${fileName}` : 'Upload Scene'}
            </Button>
          </label>
        </Grid>
        <Grid size={12}>
          <Button
            variant="contained"
            onClick={props.onDownload}
            startIcon={<DownloadIcon />}
          >Download Scene Data</Button>
        </Grid>
        <Grid size={12}>
          <Button
            variant="contained"
            onClick={() => {
              window.open(
                "https://s3-us-west-2.amazonaws.com/constructionyard.ca/student-template.zip",
                "_blank",
              );
            }}
            startIcon={<DownloadIcon />}
          >Download Project Template</Button>
        </Grid>
      </Grid>
    </Modal >
  )
}
